import { firstOrdinaryColumn } from './model';
import { useBoardDetail, useBoards } from './api';
import type { Board, Column } from './types';

interface Preferred {
  boardId: string | null;
  columnId: string | null;
}

/**
 * "Where does the new card go": the chosen board and column with the usual fallbacks
 * (explicit choice, then the preferred/default destination, then the board's default column,
 * then the first ORDINARY column - the system Calendar column is never a default).
 */
export function useDestination(options: {
  workspaceId: string | null;
  /** Create on this board only. */
  fixedBoardId?: string;
  preferred?: Preferred | null;
  boardChoice: string | null;
  columnChoice: string | null;
}) {
  const { workspaceId, fixedBoardId, preferred = null, boardChoice, columnChoice } = options;
  // A board-only guest cannot list the workspace's boards: a fixed board comes from itself.
  const listed = useBoards(workspaceId ?? '', workspaceId !== null && fixedBoardId === undefined);
  const fixed = useBoardDetail(fixedBoardId ?? '', fixedBoardId !== undefined);
  const boards =
    fixedBoardId === undefined
      ? listed
      : { isPending: fixed.isPending, isError: fixed.isError, error: fixed.error };
  const boardList: Board[] =
    fixedBoardId === undefined ? (listed.data ?? []) : fixed.data ? [fixed.data.board] : [];
  const boardId =
    boardList.find((board) => board.id === boardChoice)?.id ??
    boardList.find((board) => board.id === preferred?.boardId)?.id ??
    boardList[0]?.id ??
    '';
  const board = boardList.find((candidate) => candidate.id === boardId);
  const detail = useBoardDetail(boardId, boardId !== '');
  const columns: Column[] = detail.data?.columns ?? [];
  const preferredColumn = preferred?.boardId === boardId ? preferred.columnId : null;
  const columnId =
    columns.find((column) => column.id === columnChoice)?.id ??
    columns.find((column) => column.id === preferredColumn)?.id ??
    columns.find((column) => column.id === board?.default_column_id)?.id ??
    firstOrdinaryColumn(columns)?.id ??
    '';
  return { boards, boardList, boardId, board, detail, columns, columnId };
}
